// File: frontend/src/components/entries/ChipEditor.tsx
import React, { useCallback, useEffect, useImperativeHandle, useRef } from "react";
import "./ChipEditor.css";

/**
 * Entry input that renders reference tokens as chips instead of raw text.
 *
 * Entry content is stored as plain text containing tokens such as
 * "(baslik: kaka||81)". That tail is what makes a reference survive a later
 * rename — it resolves on the id, not on the label — but it is noise for the
 * person writing the entry, who only cares that they linked to "kaka". This
 * component keeps the stored format exactly as it is and changes only what the
 * writer sees: each token is drawn as a single chip showing its label, while
 * the id travels along invisibly in a data attribute.
 *
 * WHY A CONTENTEDITABLE AND NOT A TEXTAREA
 * A textarea renders text and nothing else — it cannot contain elements, so a
 * chip is impossible inside one. A contenteditable div can, at the cost of
 * having to handle caret positions, pasting and serialisation by hand, which
 * is what most of this file is.
 *
 * WHY IT EXPOSES A TEXTAREA-LIKE HANDLE
 * EntryEditorToolbar inserts tokens at the caret through
 * `ref.current.selectionStart` / `.selectionEnd` / `.focus()`, in about eight
 * places. Rather than rewrite all of them against DOM Ranges, this component
 * exposes those same three members, mapped onto offsets in the PLAIN TEXT the
 * editor serialises to. The toolbar therefore needs no changes at all, and the
 * offsets it computes against `content` line up with what it sets here.
 *
 * WHY IT IS UNCONTROLLED WHILE TYPING
 * Re-rendering a contenteditable on every keystroke destroys the browser's
 * native undo stack and fights the IME on mobile keyboards. So the DOM is
 * written only when the incoming value differs from what this component last
 * serialised — i.e. on mount, on an external change such as a toolbar
 * insertion or a loaded draft, and never as a result of ordinary typing.
 *
 * ONE WALK, THREE ANSWERS (the rule this file lives by)
 * Every position bug this editor ever had came from the same root cause:
 * three separate tree walks — one that produced the plain text, one that
 * turned a DOM position into a text offset, and one that turned a text offset
 * back into a DOM position — that did NOT agree with each other. The clearest
 * example: the serialiser emitted "\n" when it entered a DIV or P (the blocks
 * a browser leaves behind after a paste or an autocorrect), while the offset
 * mapper did not count that newline at all. Every such block shifted the
 * reported caret one character to the left, so a selection made near the end
 * of a long entry was read from a completely different place in the string —
 * which is how selecting one word could pre-fill the reference picker with
 * another, and how an inserted token could land in the middle of a word.
 *
 * So there is now exactly ONE walk — `scan` below. It produces the plain text,
 * the caret positions and the chip spans in a single pass, from one set of
 * rules. Nothing in this file may compute a text offset any other way.
 */

export interface ChipEditorHandle {
  /** Caret start, as an offset into the serialised plain text. */
  selectionStart: number;
  /** Caret end, as an offset into the serialised plain text. */
  selectionEnd: number;
  focus(): void;
}

export interface ChipEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  className?: string;
  onFocus?: () => void;
}

/**
 * Reference tokens that become chips. Everything else — (spoiler: ...),
 * (alinti: ...), (gorsel: ...), (baglanti: ...) — stays as literal text,
 * because those are content the writer edits rather than an opaque pointer to
 * another page.
 *
 * Group 1 is the whole token, group 2 the keyword, group 3 the label. The
 * optional "bkz-" prefix is part of the keyword so both insert modes chip.
 */
const CHIP_TOKEN_PATTERN =
  /(\((bkz-)?(mac|kisi|kulup|takim|baslik):\s*([^|)]*)\|\|[^)\s]*\))/g;

/** Turkish labels shown on the chip, keyed by token keyword. */
/**
 * Zero-width space parked after a trailing chip.
 *
 * A chip is an element, so when one is the last thing in the editor there is
 * no text position after it for the caret to occupy — the browser then draws
 * the caret at the edge of the line box, which on a phone lands visibly
 * outside the input's border. One invisible character gives it somewhere to
 * sit. It is stripped during serialisation, so it never reaches the stored
 * entry, the character counter, or anything that parses tokens.
 */
const CARET_ANCHOR = "\u200B";

/**
 * Marks the trailing filler <br>.
 *
 * A <br> that ends the content terminates its line but does NOT open the empty
 * line after it: no browser will draw a caret past a trailing <br> unless a
 * second one follows it. That is why the first Enter looked like it did
 * nothing — the value already held the newline (the counter went up), but the
 * caret stayed on the line above, and only a second Enter made an empty line
 * visible. The filler is that second <br>. It is a rendering artefact only:
 * `scan` skips it, so it contributes no newline to the serialised text, no
 * caret position of its own, and nothing to the character counter.
 */
const FILLER_BR_ATTRIBUTE = "filler";

const isFillerBr = (node: Node | null | undefined): boolean =>
  node !== null &&
  node !== undefined &&
  node.nodeType === Node.ELEMENT_NODE &&
  (node as HTMLElement).tagName === "BR" &&
  (node as HTMLElement).dataset[FILLER_BR_ATTRIBUTE] !== undefined;

/** Length of a string as the serialised value sees it — anchors do not count. */
const visibleLength = (text: string): number =>
  text.length - (text.split(CARET_ANCHOR).length - 1);

/** The same string as the serialised value sees it. */
const stripAnchors = (text: string): string => text.split(CARET_ANCHOR).join("");

const CHIP_KIND_LABELS: Record<string, string> = {
  mac: "maç",
  kisi: "kişi",
  kulup: "kulüp",
  takim: "takım",
  baslik: "başlık",
};

/** A DOM position the caret can legally occupy, with its plain-text offset. */
interface CaretPoint {
  node: Node;
  /** Index inside the node: character index for text, child index otherwise. */
  offset: number;
  /** Where this position falls in the serialised plain text. */
  textOffset: number;
  /**
   * True when the position lives INSIDE a text node. Mobile WebKit and Blink
   * cannot draw a caret on a boundary between element children, so whenever
   * several positions share a text offset the one inside a text node is the
   * one to use.
   */
  inText: boolean;
}

/** Where a chip sits in the serialised plain text. */
interface ChipSpan {
  el: HTMLElement;
  start: number;
  end: number;
}

interface ScanResult {
  /** The serialised plain text — exactly what the parent stores. */
  text: string;
  /** Every caret position, in document order. Empty unless requested. */
  points: CaretPoint[];
  /** Every chip, with its span in `text`. */
  chips: ChipSpan[];
  /** Text offset of the requested DOM position, or null if it was not found. */
  offset: number | null;
}

interface ScanOptions {
  /** Collect every caret position. Only needed when placing a caret. */
  collectPoints?: boolean;
  /** DOM position to translate into a text offset, if any. */
  container?: Node | null;
  containerOffset?: number;
}

/**
 * The single tree walk this component uses for everything.
 *
 * It serialises the DOM to plain text and, in the same pass, records where
 * each caret position and each chip falls in that text. Because the newline
 * rules, the anchor-stripping and the chip-token expansion are all applied
 * once, in one place, the three answers can never drift apart.
 */
const scan = (host: HTMLElement, options: ScanOptions = {}): ScanResult => {
  const { collectPoints = false, container = null, containerOffset = 0 } = options;

  let text = "";
  const points: CaretPoint[] = [];
  const chips: ChipSpan[] = [];
  let offset: number | null = null;

  const mark = (node: Node, nodeOffset: number, inText: boolean) => {
    if (collectPoints) {
      points.push({ node, offset: nodeOffset, textOffset: text.length, inText });
    }
    if (offset === null && container !== null && node === container && nodeOffset === containerOffset) {
      offset = text.length;
    }
  };

  const walkChildren = (parent: Node) => {
    const children = Array.from(parent.childNodes);
    children.forEach((child, index) => {
      // The position BEFORE the filler is marked as usual — that is exactly
      // the spot on the trailing empty line the caret has to reach.
      mark(parent, index, false);
      // The filler itself contributes no text, and there is no legal caret
      // position after it, so it is neither serialised nor walked into.
      if (isFillerBr(child)) return;
      walk(child);
    });
    // Skipped when the last child is the filler: its own mark above already
    // covers that offset, and a second point at the same offset would win in
    // setCaret (it takes the last match) and park the caret past the filler,
    // where the browser cannot draw it — the very bug this fixes.
    if (!isFillerBr(children[children.length - 1])) {
      mark(parent, children.length, false);
    }
  };

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? "";
      // Fast path: nothing here needs a per-character position.
      if (!collectPoints && node !== container) {
        text += stripAnchors(raw);
        return;
      }
      for (let i = 0; i < raw.length; i++) {
        mark(node, i, true);
        if (raw[i] !== CARET_ANCHOR) text += raw[i];
      }
      mark(node, raw.length, true);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;

    // A chip contributes its original token, not its visible label.
    const token = el.dataset.token;
    if (token !== undefined) {
      const start = text.length;
      text += token;
      chips.push({ el, start, end: text.length });
      return;
    }

    if (el.tagName === "BR") {
      text += "\n";
      return;
    }

    // Browsers wrap lines in DIV or P when Enter is pressed, and leave such
    // blocks behind after a paste. Each one starts a new line, except the very
    // first. This rule is why a separate offset walk was never able to agree
    // with the serialiser — here there is only one walk, so it always does.
    const isBlock = el.tagName === "DIV" || el.tagName === "P";
    if (isBlock && text !== "" && !text.endsWith("\n")) {
      text += "\n";
    }

    walkChildren(el);
  };

  walkChildren(host);

  return { text, points, chips, offset };
};

/**
 * Text offset of a DOM position.
 *
 * `bias` only matters for a position the walk cannot see — one inside a chip's
 * own label, which happens when the browser selects the chip as a whole unit.
 * The caret can never sit inside a chip, so such a position resolves to the
 * chip's near edge.
 */
const resolveOffset = (
  result: ScanResult,
  container: Node,
  bias: "start" | "end"
): number => {
  if (result.offset !== null) return result.offset;
  const chip = result.chips.find((c) => c.el === container || c.el.contains(container));
  if (chip) return bias === "start" ? chip.start : chip.end;
  return result.text.length;
};

/**
 * Steps back one whole character, so deleting never splits a surrogate pair
 * (an emoji would otherwise turn into a broken half-character).
 */
const stepBack = (text: string, index: number): number => {
  if (index <= 0) return 0;
  const code = text.charCodeAt(index - 1);
  if (code >= 0xdc00 && code <= 0xdfff && index >= 2) {
    const prev = text.charCodeAt(index - 2);
    if (prev >= 0xd800 && prev <= 0xdbff) return index - 2;
  }
  return index - 1;
};

/** Steps forward one whole character, for the same reason as stepBack. */
const stepForward = (text: string, index: number): number => {
  if (index >= text.length) return text.length;
  const code = text.charCodeAt(index);
  if (code >= 0xd800 && code <= 0xdbff && index + 1 < text.length) {
    const next = text.charCodeAt(index + 1);
    if (next >= 0xdc00 && next <= 0xdfff) return index + 2;
  }
  return index + 1;
};

/**
 * Adds or removes the trailing filler <br> so it is present exactly when the
 * content ends with a newline.
 *
 * Called after every write to the DOM — the full redraw in renderValue and the
 * cheap path in handleInput, where the writer typed on the empty last line and
 * the filler is no longer needed (or deleted the last character and it is
 * needed again). Since `scan` ignores the filler, adding or removing it never
 * changes the serialised text.
 */
const syncTrailingFiller = (host: HTMLElement, text: string): void => {
  const last = host.lastChild;
  const hasFiller = isFillerBr(last);
  const needsFiller = text.endsWith("\n");

  if (needsFiller && !hasFiller) {
    const filler = document.createElement("br");
    filler.dataset[FILLER_BR_ATTRIBUTE] = "true";
    host.appendChild(filler);
    return;
  }
  if (!needsFiller && hasFiller && last) {
    host.removeChild(last);
  }
};

/**
 * True when the DOM has drifted from the shape this component renders.
 *
 * Mobile keyboards, autocorrect, drag-and-drop and the browser's own paste
 * handling all like to leave DIV wrappers, styling spans or bare chips with
 * their anchors eaten. None of that breaks the serialised text — `scan`
 * understands it — but it does make the caret behave unpredictably, so the
 * editor redraws itself from its own text whenever it sees any of it.
 */
const needsNormalisation = (host: HTMLElement): boolean => {
  const elements = Array.from(host.querySelectorAll("*"));
  for (const node of elements) {
    const el = node as HTMLElement;
    if (el.tagName === "BR") continue;
    if (el.dataset.token !== undefined) {
      // A chip must keep a text node on each side — see renderValue.
      const before = el.previousSibling;
      const after = el.nextSibling;
      const hasBefore =
        before !== null &&
        before.nodeType === Node.TEXT_NODE &&
        (before.textContent ?? "").includes(CARET_ANCHOR);
      const hasAfter =
        after !== null &&
        after.nodeType === Node.TEXT_NODE &&
        (after.textContent ?? "").includes(CARET_ANCHOR);
      if (!hasBefore || !hasAfter) return true;
      continue;
    }
    return true;
  }
  return false;
};

export const ChipEditor = React.forwardRef<ChipEditorHandle, ChipEditorProps>(
  ({ value, onChange, placeholder, disabled, maxLength, className, onFocus }, ref) => {
    const hostRef = useRef<HTMLDivElement | null>(null);

    // The last text this component itself produced. Used to tell an external
    // value change (toolbar insertion, draft load, parent reset) apart from the
    // echo of the writer's own typing, which must not trigger a re-render.
    const lastSerializedRef = useRef<string>("");

    // Latest incoming value, readable from the imperative handle's setters
    // (which are not re-created on every render).
    const valueRef = useRef<string>(value);
    valueRef.current = value;

    // Latest onChange, for the native listeners registered once below.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // Latest maxLength, for the same reason.
    const maxLengthRef = useRef<number | undefined>(maxLength);
    maxLengthRef.current = maxLength;

    // True while an IME composition is in flight. Nothing may rewrite the DOM
    // during one: doing so cancels the composition and loses the half-typed
    // word, which on a phone keyboard means almost every word.
    const composingRef = useRef(false);

    // Last caret position seen while this editor had the selection.
    //
    // A <textarea> keeps its selectionStart/End after it loses focus, so a
    // toolbar that reads them once the user has clicked a button still gets
    // the place the caret was. A contenteditable does not: clicking a toolbar
    // button (or opening a reference picker) moves the browser selection out
    // of this element, and the live selection then reports nothing usable.
    // Reading it at that moment is what made every insertion land at the very
    // start of the entry. So the position is recorded whenever the selection
    // IS inside this editor, and that record is what the handle reports once
    // it no longer is.
    const rememberedCaretRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

    // Caret position waiting for the DOM to catch up — see applyCaret below.
    const pendingCaretRef = useRef<number | null>(null);

    // ---------------------------------------------------------------------
    // Serialisation: DOM -> plain text
    // ---------------------------------------------------------------------

    const serialize = useCallback((host: HTMLElement): string => scan(host).text, []);

    // ---------------------------------------------------------------------
    // Caret <-> plain-text offset mapping
    // ---------------------------------------------------------------------

    /**
     * Plain-text offset of a DOM position, counting chips as their full token
     * length so offsets match the string the toolbar works with.
     */
    const offsetOf = useCallback(
      (host: HTMLElement, container: Node, containerOffset: number, bias: "start" | "end" = "start"): number => {
        const result = scan(host, { container, containerOffset });
        return resolveOffset(result, container, bias);
      },
      []
    );

    /** Places the caret at a plain-text offset. */
    const setCaret = useCallback((host: HTMLElement, target: number) => {
      const selection = window.getSelection();
      if (!selection) return;

      const { text, points } = scan(host, { collectPoints: true });
      const wanted = Math.max(0, Math.min(target, text.length));

      // Several DOM positions can share one text offset: the boundary between
      // host children, the zero-width anchors around a chip, and the first
      // index of the next real text node all describe the same spot. Prefer
      // the last position that lives inside a text node — that is the only
      // kind a mobile browser reliably draws a caret on, and taking the LAST
      // one parks the caret after an anchor rather than before it, which is
      // what makes it appear beside the chip instead of on the line's edge.
      let fallback: CaretPoint | null = null;
      let exactAny: CaretPoint | null = null;
      let exactInText: CaretPoint | null = null;

      for (const point of points) {
        if (point.textOffset > wanted) break;
        fallback = point;
        if (point.textOffset === wanted) {
          exactAny = point;
          if (point.inText) exactInText = point;
        }
      }

      const chosen = exactInText ?? exactAny ?? fallback;
      const range = document.createRange();

      if (chosen) {
        range.setStart(chosen.node, chosen.offset);
        range.collapse(true);
      } else {
        // Empty editor: put the caret at the very start.
        range.selectNodeContents(host);
        range.collapse(true);
      }

      selection.removeAllRanges();
      selection.addRange(range);
    }, []);

    // ---------------------------------------------------------------------
    // Rendering: plain text -> DOM
    // ---------------------------------------------------------------------

    /**
     * Places the caret right beside a chip (before it or after it).
     *
     * This exists because clicking on a chip does not naturally produce a
     * caret at all. contentEditable="false" children inside a contentEditable
     * host are treated as atomic "widgets" for hit-testing — the same way
     * ProseMirror/Slate-style editors work — so a single click on one, or on
     * its own padding right at its edge, makes the browser select the whole
     * node (a highlighted block) instead of dropping a caret next to it.
     * Handling the pointer event directly and resolving to the chip's own text
     * offset fixes that for every input type — mouse, touch, and pen — and
     * routes through the same caret placement everything else uses.
     */
    const placeCaretBesideChip = useCallback((chip: HTMLElement, before: boolean) => {
      const host = hostRef.current;
      if (!host) return;

      const { chips } = scan(host);
      const span = chips.find((c) => c.el === chip);
      if (!span) return;

      const position = before ? span.start : span.end;
      host.focus();
      setCaret(host, position);
      rememberedCaretRef.current = { start: position, end: position };
    }, [setCaret]);

    const renderValue = useCallback((host: HTMLElement, text: string) => {
      host.textContent = "";

      const appendText = (chunk: string) => {
        // Split on newlines so they become real line breaks rather than
        // collapsing; white-space: pre-wrap keeps the rest of the spacing.
        const parts = chunk.split("\n");

        parts.forEach((part, index) => {
          if (index > 0) host.appendChild(document.createElement("br"));
          if (part !== "") host.appendChild(document.createTextNode(part));
        });
      };

      let cursor = 0;
      CHIP_TOKEN_PATTERN.lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = CHIP_TOKEN_PATTERN.exec(text)) !== null) {
        if (match.index > cursor) appendText(text.slice(cursor, match.index));

        const [token, , bkzPrefix, keyword, rawLabel] = match;

        // An anchor BEFORE every chip too, not only a trailing one.
        //
        // A chip is contentEditable="false", so the position right before it
        // is a boundary between host children (or the very start of a line)
        // rather than a spot inside a text node — exactly like the "after"
        // case below, but on the other side. Without this, a chip that opens
        // a line (or the whole entry) has no text node to its left, so
        // clicking in the blank space before it cannot place the caret there
        // at all; the click is simply dropped. One zero-width space gives
        // that position a text node to live in, on both mouse clicks and
        // programmatic caret placement (see setCaret above).
        host.appendChild(document.createTextNode(CARET_ANCHOR));

        const chip = document.createElement("span");
        chip.className = "chip-editor__chip";
        chip.contentEditable = "false";
        // The full original token. serialize() reads this back, so the stored
        // format is byte-identical to what a plain textarea would have held.
        chip.dataset.token = token;
        chip.title = CHIP_KIND_LABELS[keyword] ?? keyword;

        // Label only. An earlier version also printed the kind ("maç",
        // "başlık", ...) inside the chip, which roughly doubled its width for
        // information the label already conveys. The kind stays available on
        // hover through the title attribute set above.
        chip.textContent = (bkzPrefix ? "bkz: " : "") + rawLabel.trim();

        // Override the browser's default "select the whole atomic node"
        // behaviour on click/tap so a click near either edge parks a caret
        // there instead — see placeCaretBesideChip's comment for why this is
        // necessary. preventDefault on pointerdown, before native selection
        // handling runs, is what stops the node-selection from happening at
        // all — a click handler alone would fire too late.
        chip.addEventListener("pointerdown", (event) => {
          event.preventDefault();
          const rect = chip.getBoundingClientRect();
          const clickedLeftHalf = event.clientX < rect.left + rect.width / 2;
          placeCaretBesideChip(chip, clickedLeftHalf);
        });

        host.appendChild(chip);

        // An anchor after EVERY chip, not only a trailing one.
        //
        // A chip is contentEditable="false", so the position right after it is
        // a boundary between host children rather than a spot inside a text
        // node. Mobile WebKit and Blink cannot draw a caret there: they fall
        // back to the start of the line box, which is why the caret appeared
        // to the LEFT of the chip and only snapped right once a character was
        // typed and a real text node existed. One zero-width space gives every
        // post-chip position a text node to live in. It is stripped during
        // serialisation and skipped by the offset math above, so it is
        // invisible to the stored entry, the character counter and the toolbar.
        host.appendChild(document.createTextNode(CARET_ANCHOR));

        cursor = match.index + token.length;
      }

      if (cursor < text.length) appendText(text.slice(cursor));

      syncTrailingFiller(host, text);
    }, [placeCaretBesideChip]);

    // ---------------------------------------------------------------------
    // Committing a model-level edit
    // ---------------------------------------------------------------------

    /**
     * Applies a text change this component computed itself (a deletion, a line
     * break, a paste) rather than one the browser performed.
     *
     * The DOM is redrawn here and now instead of waiting for the parent's
     * state to come back, so the caret lands in the same frame as the edit and
     * never flickers through an intermediate position. If the parent alters
     * the value on its way back, the sync effect below redraws again and
     * re-applies the stashed caret.
     */
    const commit = useCallback((nextText: string, caretOffset: number) => {
      const host = hostRef.current;
      pendingCaretRef.current = caretOffset;

      if (host) {
        renderValue(host, nextText);
        lastSerializedRef.current = nextText;
        rememberedCaretRef.current = { start: caretOffset, end: caretOffset };
        setCaret(host, caretOffset);
      }

      onChangeRef.current(nextText);
    }, [renderValue, setCaret]);

    // ---------------------------------------------------------------------
    // Textarea-like handle for EntryEditorToolbar
    // ---------------------------------------------------------------------

    useImperativeHandle(ref, () => {
      const handle = {
        focus() {
          hostRef.current?.focus();
        },
      } as ChipEditorHandle;

      const readCaret = (which: "start" | "end"): number => {
        const host = hostRef.current;
        const selection = window.getSelection();
        const remembered = rememberedCaretRef.current;

        // Selection has moved elsewhere (a toolbar button, a picker dialog) —
        // report where the caret was when it was last in here.
        if (!host || !selection || selection.rangeCount === 0) {
          return which === "start" ? remembered.start : remembered.end;
        }
        const range = selection.getRangeAt(0);
        if (!host.contains(range.startContainer)) {
          return which === "start" ? remembered.start : remembered.end;
        }

        return which === "start"
          ? offsetOf(host, range.startContainer, range.startOffset, "start")
          : offsetOf(host, range.endContainer, range.endOffset, "end");
      };

      // The toolbar sets new content and then assigns a caret position from
      // inside a requestAnimationFrame. That frame can run BEFORE React has
      // flushed the effect that redraws this editor, so the DOM is not
      // necessarily showing the new content yet.
      //
      // Applying the caret to the stale DOM and then letting the redraw happen
      // is exactly what threw the caret back to the start. So the position is
      // only applied here when the DOM is known to be in sync — i.e. the last
      // text this component rendered equals the value it has been given.
      // Otherwise it is stashed, and the sync effect applies it right after it
      // redraws.
      const applyCaret = (position: number) => {
        const host = hostRef.current;
        if (!host || lastSerializedRef.current !== valueRef.current) {
          pendingCaretRef.current = position;
          return;
        }
        pendingCaretRef.current = null;
        rememberedCaretRef.current = { start: position, end: position };
        host.focus();
        setCaret(host, position);
      };

      Object.defineProperty(handle, "selectionStart", {
        get: () => readCaret("start"),
        set: applyCaret,
      });
      Object.defineProperty(handle, "selectionEnd", {
        get: () => readCaret("end"),
        set: applyCaret,
      });

      return handle;
    }, [offsetOf, setCaret]);

    // Keep rememberedCaretRef current while the selection is inside this
    // editor. selectionchange is the only event that fires for every way the
    // caret can move — typing, arrow keys, clicking, dragging a selection —
    // so one listener covers them all.
    useEffect(() => {
      const onSelectionChange = () => {
        const host = hostRef.current;
        const selection = window.getSelection();
        if (!host || !selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!host.contains(range.startContainer)) return;
        rememberedCaretRef.current = {
          start: offsetOf(host, range.startContainer, range.startOffset, "start"),
          end: offsetOf(host, range.endContainer, range.endOffset, "end"),
        };
      };
      document.addEventListener("selectionchange", onSelectionChange);
      return () => document.removeEventListener("selectionchange", onSelectionChange);
    }, [offsetOf]);

    // ---------------------------------------------------------------------
    // Syncing external value changes into the DOM
    // ---------------------------------------------------------------------

    useEffect(() => {
      const host = hostRef.current;
      if (!host) return;
      // Ignore the echo of the writer's own typing; only external changes
      // (toolbar insertion, draft load, form reset) reach the DOM here.
      if (value === lastSerializedRef.current) {
        // The DOM already matches. Anything stashed for a redraw that never
        // needed to happen would otherwise fire at the next unrelated change.
        pendingCaretRef.current = null;
        return;
      }

      renderValue(host, value);
      lastSerializedRef.current = value;

      const pending = pendingCaretRef.current;
      if (pending !== null) {
        pendingCaretRef.current = null;
        rememberedCaretRef.current = { start: pending, end: pending };
        host.focus();
        setCaret(host, pending);
      }
    }, [value, renderValue, setCaret]);

    // ---------------------------------------------------------------------
    // Input handling
    // ---------------------------------------------------------------------

    /** Current selection as a pair of plain-text offsets. */
    const readSelectionRange = useCallback((host: HTMLElement): { start: number; end: number } => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return { ...rememberedCaretRef.current };
      }
      const range = selection.getRangeAt(0);
      if (!host.contains(range.startContainer) || !host.contains(range.endContainer)) {
        return { ...rememberedCaretRef.current };
      }
      const start = offsetOf(host, range.startContainer, range.startOffset, "start");
      const end = offsetOf(host, range.endContainer, range.endOffset, "end");
      return start <= end ? { start, end } : { start: end, end: start };
    }, [offsetOf]);

    /** Replaces the current selection with text, through the model. */
    const replaceSelection = useCallback((insertText: string) => {
      const host = hostRef.current;
      if (!host) return;

      const { text } = scan(host);
      const { start, end } = readSelectionRange(host);
      const limit = maxLengthRef.current;
      if (limit && text.length - (end - start) + insertText.length > limit) return;

      const next = text.slice(0, start) + insertText + text.slice(end);
      commit(next, start + insertText.length);
    }, [commit, readSelectionRange]);

    const handleInput = useCallback(() => {
      const host = hostRef.current;
      if (!host) return;
      // An IME composition is still open; the DOM is mid-word and must not be
      // read back or rewritten yet. compositionend re-runs this.
      if (composingRef.current) return;

      if (needsNormalisation(host)) {
        // The browser left something behind that this component does not draw
        // itself. Redraw from the serialised text so the chip/anchor structure
        // — and therefore every caret position — is predictable again.
        const result = scan(host, { collectPoints: false });
        const { start } = readSelectionRange(host);
        renderValue(host, result.text);
        lastSerializedRef.current = result.text;
        rememberedCaretRef.current = { start, end: start };
        setCaret(host, start);
        onChangeRef.current(result.text);
        return;
      }

      const text = serialize(host);
      // Typing on the trailing empty line makes the filler redundant;
      // deleting the last character of a line makes it necessary again.
      // Neither changes the serialised text, so no redraw is involved.
      syncTrailingFiller(host, text);
      lastSerializedRef.current = text;
      onChangeRef.current(text);

      // selectionchange usually fires alongside typing, but not for every
      // input type on every browser — recording here as well keeps the
      // remembered position from lagging a character behind.
      rememberedCaretRef.current = readSelectionRange(host);
    }, [readSelectionRange, renderValue, serialize, setCaret]);

    /**
     * Native beforeinput listener.
     *
     * React's synthetic onBeforeInput does not report every inputType on every
     * mobile browser, and the deletions handled here are exactly the events
     * mobile keyboards send. Registering natively is the only way to see them
     * all.
     *
     * Deletions next to a chip are handled through the model rather than left
     * to the browser. Natively, a Backspace beside a chip first eats the
     * invisible anchor and only removes the chip on the second press — which
     * is why deleting a reference used to take two taps. Working on the text
     * instead removes the whole token in one, and does the same for a
     * character that happens to sit on the far side of an anchor.
     */
    useEffect(() => {
      const host = hostRef.current;
      if (!host) return;

      const onBeforeInput = (event: InputEvent) => {
        if (composingRef.current) return;
        const type = event.inputType;

        if (type === "insertParagraph" || type === "insertLineBreak") {
          // Keep line breaks as <br> rather than letting the browser wrap the
          // line in a DIV, which keeps the serialised text predictable.
          event.preventDefault();
          replaceSelection("\n");
          return;
        }

        if (type === "deleteContentBackward" || type === "deleteContentForward") {
          const selection = window.getSelection();
          if (!selection || selection.rangeCount === 0) return;
          const range = selection.getRangeAt(0);
          if (!host.contains(range.startContainer)) return;
          // A non-empty selection deletes as a block; chips inside it are
          // atomic, so the browser handles that case correctly on its own.
          if (!range.collapsed) return;

          const result = scan(host, { container: range.startContainer, containerOffset: range.startOffset });
          const caret = resolveOffset(result, range.startContainer, "start");
          const text = result.text;
          const backward = type === "deleteContentBackward";

          const chip = backward
            ? result.chips.find((c) => c.end === caret)
            : result.chips.find((c) => c.start === caret);

          event.preventDefault();

          if (chip) {
            commit(text.slice(0, chip.start) + text.slice(chip.end), chip.start);
            return;
          }

          if (backward) {
            if (caret <= 0) return;
            const from = stepBack(text, caret);
            commit(text.slice(0, from) + text.slice(caret), from);
          } else {
            if (caret >= text.length) return;
            const to = stepForward(text, caret);
            commit(text.slice(0, caret) + text.slice(to), caret);
          }
          return;
        }

        // Length guard. Only additions are limited; deletions always go
        // through, or an entry at the limit could never be shortened.
        const limit = maxLengthRef.current;
        if (!limit || type.startsWith("delete") || type.startsWith("history")) return;

        const { start, end } = (() => {
          const selection = window.getSelection();
          if (!selection || selection.rangeCount === 0) return rememberedCaretRef.current;
          const range = selection.getRangeAt(0);
          if (!host.contains(range.startContainer)) return rememberedCaretRef.current;
          const s = scan(host, { container: range.startContainer, containerOffset: range.startOffset });
          const startOffset = resolveOffset(s, range.startContainer, "start");
          const e = scan(host, { container: range.endContainer, containerOffset: range.endOffset });
          const endOffset = resolveOffset(e, range.endContainer, "end");
          return startOffset <= endOffset
            ? { start: startOffset, end: endOffset }
            : { start: endOffset, end: startOffset };
        })();

        const incoming = event.data ? event.data.length : 1;
        if (scan(host).text.length - (end - start) + incoming > limit) {
          event.preventDefault();
        }
      };

      host.addEventListener("beforeinput", onBeforeInput);
      return () => host.removeEventListener("beforeinput", onBeforeInput);
    }, [commit, replaceSelection]);

    const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
      // Always paste as plain text, and through the model rather than through
      // execCommand: the browser's own insertion is what wrapped pasted lines
      // in DIV blocks, and those blocks are the structure the offset mapping
      // used to disagree about. Going through the text keeps the DOM in the
      // single shape renderValue produces.
      event.preventDefault();
      const text = event.clipboardData.getData("text/plain");
      if (!text) return;
      replaceSelection(text.replace(/\r\n?/g, "\n"));
    }, [replaceSelection]);

    const handleCompositionStart = useCallback(() => {
      composingRef.current = true;
    }, []);

    const handleCompositionEnd = useCallback(() => {
      composingRef.current = false;
      handleInput();
    }, [handleInput]);

    const isEmpty = value === "";

    return (
      <div
        ref={hostRef}
        className={`chip-editor${className ? ` ${className}` : ""}${disabled ? " chip-editor--disabled" : ""}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        data-empty={isEmpty ? "true" : "false"}
        onInput={handleInput}
        onPaste={handlePaste}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onFocus={onFocus}
      />
    );
  }
);

ChipEditor.displayName = "ChipEditor";